import React, { useState, useEffect, useRef } from 'react';
import defaultProjectPlaceHolder from '../../assets/images/project_placeholder@2x.jpg';
import Image from '../Image';
import { ArrowDownNarrowWide, CircleMinusIcon, CircleOffIcon, EyeOffIcon, PencilLineIcon, RefreshCcwIcon, XIcon } from 'lucide-react';
import { showToast } from '../shared/Toast';
import { Group } from './Directory';
import Breadcrumb from '../shared/Breadcrumb';
import UserIcon from '../submission_boards/component/UserIcon';
import UserFilter from './UserFilter';
import FilePreviewer from '../submission_kits/components/FilePreviewer';
import userService from '../services/UserService';
import Button from '../submission_boards/component/Button';
import { useIsMobile } from '../submission_boards/component/useIsMobile';
import { ProfileDetailView } from '../profiles/ProfileViews';
import userSkillsService, { UserSkill } from '../services/UserSkillsService';

interface User {
  id: number;
  uuid: string;
  firstName: string;
  lastName: string;
  country: string;
  ethnicities: string[];
  gender: string;
  height: number;
  weight: number;
  avatarUrl: string;
  croppedAvatarUrl: string;
  measure_system: 'imperial' | 'metric';
  subscription_status?: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'incomplete';
  subscription_tier?: 'free' | 'pro' | 'premium';
}

interface CollectionDetailProps {
  collection: Group;
  showSharedCollection?: boolean;
  onSave: (saved: Group) => void;
  onBack: (refreshGroups: boolean) => void;
  onShowShareModal: () => void;
  favoriteUsers: any;
  onToggleFavorite: (userUuid: string, event: React.MouseEvent, user: any) => void;
  // onAddToCollection: (userUuid: string) => void;
  refreshCollection: (loadFav: boolean, loadCollection: boolean) => void;
  currentUser?: any;
  // allAvailableUsers?: User[]; // Add this prop to get available users
  measurementRanges?: any; // Add this for filters
}

interface GroupContextMenuProps {
  user: User;
  groups: Group[];
  onAddToGroup: (groupId: number) => void;
  onOpenCreateModal: () => void;
  onClose: () => void;
  isCreating: boolean;
}

// FiltersContent component from directory
const FiltersContent = ({
  addFavoritesFilters,
  setAddFavoritesFilters,
  addFavoritesSearchTerm,
  setAddFavoritesSearchTerm,
  handleClearFilters,
  selectedCollectionFilter,
  setSelectedCollectionFilter,
  groups,
  measurementRanges
}: {
  addFavoritesFilters: any;
  setAddFavoritesFilters: any;
  addFavoritesSearchTerm: string;
  setAddFavoritesSearchTerm: (term: string) => void;
  handleClearFilters: () => void;
  selectedCollectionFilter: any;
  setSelectedCollectionFilter: (filter: any) => void;
  groups: Group[];
  measurementRanges: any;
}) => (
  <div className="space-y-4">
    {/* From Dropdown */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">From:</label>
      <select
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        value={selectedCollectionFilter}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'all' || value === 'favorites') {
            setSelectedCollectionFilter(value);
          } else {
            const numValue = parseInt(value);
            if (!isNaN(numValue)) {
              setSelectedCollectionFilter(numValue);
            }
          }
        }}
      >
        <option value="all">All Members</option>
        <option value="favorites">All My Favorites</option>
        {groups.filter(g => !isGroupArchived(g)).map(group => (
          <option key={group.id} value={group.id}>{group.title}</option>
        ))}
      </select>
    </div>

    {/* Search */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Search by:</label>
      <div className="relative">
        <input
          type="text"
          placeholder="Name, Last Name"
          value={addFavoritesSearchTerm}
          onChange={(e) => setAddFavoritesSearchTerm(e.target.value)}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm"
        />
        <svg className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
    </div>

    {/* Filter By Header */}
    <div className="flex items-center justify-between pt-4">
      <label className="block text-sm font-medium text-gray-700">Filter by:</label>
      <button
        onClick={handleClearFilters}
        className="text-xs text-blue-600 hover:text-blue-700"
      >
        Clear Filters
      </button>
    </div>

    {/* Gender Filter */}
    <div>
      <label className="block text-xs text-gray-600 mb-1">Gender</label>
      <select
        value={addFavoritesFilters.gender}
        onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, gender: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      >
        <option value="">All</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
        <option value="Non-binary">Non-binary</option>
      </select>
    </div>

    {/* Ethnicity Filter */}
    <div>
      <label className="block text-xs text-gray-600 mb-1">Ethnicity</label>
      <select
        value={addFavoritesFilters.ethnicity}
        onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, ethnicity: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      >
        <option value="">All</option>
        <option value="Caucasian">Caucasian</option>
        <option value="African American">African American</option>
        <option value="Asian">Asian</option>
        <option value="Hispanic">Hispanic</option>
        <option value="Middle Eastern">Middle Eastern</option>
        <option value="Pacific Islander">Pacific Islander</option>
        <option value="Native American">Native American</option>
      </select>
    </div>

    {/* Country Filter */}
    <div>
      <label className="block text-xs text-gray-600 mb-1">Country</label>
      <select
        value={addFavoritesFilters.country}
        onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, country: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      >
        <option value="">All</option>
        <option value="Australia">Australia</option>
        <option value="France">France</option>
        <option value="Italy">Italy</option>
        <option value="USA">USA</option>
        <option value="UK">UK</option>
        <option value="Canada">Canada</option>
      </select>
    </div>

    {/* Weight Filter */}
    <div>
      <label className="block text-xs text-gray-600 mb-1">Weight</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={addFavoritesFilters.weightMin}
          onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, weightMin: Number(e.target.value) }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="Min"
        />
        <span className="text-gray-500">-</span>
        <input
          type="number"
          value={addFavoritesFilters.weightMax}
          onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, weightMax: Number(e.target.value) }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="Max"
        />
      </div>
      <span className="text-xs text-gray-500 mt-1 block">Lbs</span>
    </div>

    {/* Height Filter */}
    <div>
      <label className="block text-xs text-gray-600 mb-1">Height</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={addFavoritesFilters.heightMin}
          onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, heightMin: Number(e.target.value) }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="Min"
        />
        <span className="text-gray-500">-</span>
        <input
          type="number"
          value={addFavoritesFilters.heightMax}
          onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, heightMax: Number(e.target.value) }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="Max"
        />
      </div>
      <span className="text-xs text-gray-500 mt-1 block">Ft</span>
    </div>

    {/* Add More Filters Button */}
    <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 mt-4">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
      Add More Filters
    </button>
  </div>
);

function GroupContextMenu({ user, groups, onAddToGroup, onOpenCreateModal, onClose, isCreating }: GroupContextMenuProps) {
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([]);

  const activeGroups = groups.filter(g => (g.status === 'Active' || !g.status) && !g.archived && (!g.members || g.members.indexOf(user.uuid) < 0));

  React.useEffect(() => {
    setSelectedGroupIds([]);
  }, [user?.uuid, groups]);

  const toggleSelectGroup = (groupId: number) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleDone = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectedGroupIds.length === 0) {
      onClose();
      return;
    }

    try {
      await Promise.all(selectedGroupIds.map((gid) => onAddToGroup(gid)));
    } catch (err) {
      console.error('Error adding to groups:', err);
    } finally {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-customGray z-50 flex items-center justify-center px-2 pb-2 sm:px-4 sm:pb-4 pt-20"
      // style={{ backgroundColor: '#6b7a8a' }}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full h-full sm:h-auto max-w-3xl mx-auto overflow-hidden flex flex-col"
        style={{ maxHeight: '95vh' }}
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={user?.croppedAvatarUrl || defaultProjectPlaceHolder}
                alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                Add <span className="font-bold">{user?.firstName} {user?.lastName}</span> to a Collection:
              </div>
              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                Select one or more collections where this member should be added.
              </div>
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0 ml-2"
            aria-label="Close"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Body: grid of collection cards */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {activeGroups.length === 0 && (
              <div className="col-span-full py-8 sm:py-12 text-center text-gray-500">
                No active collections yet.
              </div>
            )}

            {activeGroups.map((group) => {
              const selected = selectedGroupIds.includes(group.id);
              const groupAvatars = group.teamMemberAvatars || [];

              const main = groupAvatars[0] || defaultProjectPlaceHolder;
              const rightTop = groupAvatars[1] || defaultProjectPlaceHolder;
              const rightBottom = groupAvatars[2] || defaultProjectPlaceHolder;

              return (
                <div
                  key={group.id}
                  className={`rounded-xl bg-white border-2 ${selected ? 'border-blue-500 shadow-lg' : 'border-gray-200'
                    } flex flex-col cursor-pointer transition-all hover:shadow-md`}
                  style={{
                    padding: '12px',
                    minHeight: '220px'
                  }}
                  onClick={() => toggleSelectGroup(group.id)}
                  role="button"
                  aria-pressed={selected}
                >
                  {/* Images area */}
                  <div className="flex gap-2 mb-3" style={{ height: '150px' }}>
                    <div className="w-1/2 h-full rounded-lg overflow-hidden bg-gray-100">
                      {main ? (
                        <img
                          src={main}
                          onError={(e) => {
                            e.currentTarget.src = defaultProjectPlaceHolder;
                          }}
                          alt={group.title}
                          className="w-full h-full object-contain"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 w-1/2">
                      <div className="flex-1 rounded-lg overflow-hidden bg-gray-100">
                        {rightTop ? (
                          <img
                            src={rightTop}
                            onError={(e) => {
                              e.currentTarget.src = defaultProjectPlaceHolder;
                            }}
                            alt={`${group.title}-1`}
                            className="w-full h-full object-contain"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 rounded-lg overflow-hidden bg-gray-100">
                        {rightBottom ? (
                          <img
                            src={rightBottom}
                            onError={(e) => {
                              e.currentTarget.src = defaultProjectPlaceHolder;
                            }}
                            alt={`${group.title}-2`}
                            className="w-full h-full object-contain"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-auto">
                    <div className="text-sm sm:text-base font-semibold text-gray-900 truncate flex-1 mr-2">
                      {group.title}
                    </div>
                    <div
                      className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                        }`}
                    >
                      {selected && (
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenCreateModal(); onClose(); }}
            disabled={isCreating}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg bg-white text-sm sm:text-base font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none">
              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Create a New Collection
          </button>

          <button
            onClick={handleDone}
            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg bg-blue-600 text-white text-sm sm:text-base font-semibold hover:bg-blue-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function to check if group is archived
const isGroupArchived = (group: Group) => {
  return group.status === 'Archived' || group.archived === true;
};


interface UserItemUiProps {
  user: User;
  currentUser: any;
  collectionSelected: Group;
  isArchived: boolean;
  isOwner: boolean;
  onRemove: (user: User, collectionSelected: Group) => void;

}
export const UserItemUi: React.FC<UserItemUiProps> = ({
  user,
  currentUser,
  isArchived,
  collectionSelected,
  isOwner,
  onRemove,
}) => {
  const [profileModalUser, setprofileModalUser] = useState(null);
  const isMobile = useIsMobile();
  const divRef = useRef(null);
  const [heightImage, setHeightImage] = useState('0px');


  // Function to calculate and set the height
  const calculateHeight = () => {
    if (divRef.current) {
      const width = divRef.current.offsetWidth;
      if (width > 0) {
        const calculatedHeight = width * (5 / 4);
        setHeightImage(calculatedHeight + 'px');
      }
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateHeight();
    }, 0);

    // Add a resize listener
    window.addEventListener('resize', calculateHeight);

    // Clean up the event listener when the component unmounts
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateHeight();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [user?.croppedAvatarUrl, user?.avatarUrl]);

  return (
    <div
      className="bg-white relative w-full cursor-pointer"
      style={{
        padding: '15px 15px 0px',
        flexGrow: 0,
      }}
      onClick={() => {
        if(isArchived) {
          return;
        }
        setprofileModalUser(user)
      }}
      a-href={`/users/${user.uuid}?from=collection&collection_id=${collectionSelected.id}`}
    >
      <div
        className="relative bg-gradient-to-br  w-full"
        style={{
          aspectRatio: '4/5',
          borderRadius: '0.5rem',
          overflow: 'hidden'
        }}
      >
        <Image
          className="inset-0 w-full h-full object-cover rounded-lg"
          src={user.croppedAvatarUrl}
          fallbackSrc={defaultProjectPlaceHolder}
          alt={`${user.firstName} ${user.lastName}`}
        />

        {user.subscription_status === 'active' && (user.subscription_tier === 'premium'
          || user.subscription_tier === 'pro') && (
            <div className="absolute top-2 left-2 bg-black text-white text-xs font-semibold px-2 py-1 rounded-full z-[1]">
              Pro
            </div>
        )}
        {!isArchived && isOwner &&
          <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 flex-shrink-0" 
          onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if(onRemove) {
                                await onRemove(user, collectionSelected);
                              }
                            }}>
            <CircleMinusIcon className="w-4-2 h-4-2" aria-hidden="true" />
          </button> 
        }
      </div>

      {profileModalUser != null && (<ProfileDetailView user={profileModalUser}
        currentUser={currentUser}
        userUuid={profileModalUser.uuid}
        onClose={() => {
          setprofileModalUser(null);
        }}
      />)}
    </div>
  )
}

export default function CollectionDetail({
  collection,
  onSave,
  onBack,
  onShowShareModal,
  favoriteUsers,
  onToggleFavorite,
  // onAddToCollection,
  refreshCollection,
  currentUser,
  // allAvailableUsers = [],
  measurementRanges = {
    height: { min: 0, max: 300 },
    weight: { min: 0, max: 500 }
  },
  showSharedCollection
}: CollectionDetailProps) {

  const isMobile = useIsMobile();

  const [collectionSelected, setcollectionSelected] = useState<Group | null>(null);
  const [collectionOwner, setcollectionOwner] = useState<User | null>(null);
  const [currentlySharedWith, setCurrentlySharedWith] = useState<User[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'name-asc' | 'name-desc'>('newest');
  const [showGroupMenu, setShowGroupMenu] = useState<string | null>(null);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [manageMembersGroup, setManageMembersGroup] = useState<Group | null>(null);
  const [currentMembers, setCurrentMembers] = useState<User[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [searchMember, setSearchMember] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [editingDescriptionId, seteditingDescriptionId] = useState('');
  const [editingTitleId, seteditingTitleId] = useState(null);

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [manageMembersActiveTab, setManageMembersActiveTab] = useState<'add' | 'current'>('add');
  const [availableUsersForManage, setAvailableUsersForManage] = useState<User[]>([]);
  const [sortMenuVisible, setSortMenuVisible] = useState(false);
  const [showAddCollectSortMenu, setShowAddCollectSortMenu] = useState(false);

  // Add to Collection Modal State
  const [showAddToCollectionModal, setShowAddToCollectionModal] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const [addFavoritesSearchTerm, setAddFavoritesSearchTerm] = useState('');
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<string[]>([]);
  const [isLoadingFilteredUsers, setIsLoadingFilteredUsers] = useState(false);
  // const [filteredUsersForAdd, setFilteredUsersForAdd] = useState<User[]>([]);
  const [addFavoritesFilters, setAddFavoritesFilters] = useState({
    gender: '',
    ethnicity: '',
    country: '',
    weightMin: measurementRanges.weight.min,
    weightMax: measurementRanges.weight.max,
    heightMin: measurementRanges.height.min,
    heightMax: measurementRanges.height.max,
  });

  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filteredParams, setfilteredParams] = useState({});
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const observerAddFavRef = useRef(null);
  const [noMoreData, setNoMoreData] = useState<string>('');
  const perPage: number = 12; // Number of records to fetch per page

  const [masterSkills, setMasterSkills] = useState<UserSkill[]>([]);

  const loadMasterSkills = async () => {
    try {
      setLoading(true);
      const response = await userSkillsService.searchMasterSkills('');
      if (response.success) {
        // Filter out already selected skills
        // const selectedIds = skills.map((s) => s.master_special_skill_id).filter(Boolean);
        // let filtered = response.skills.filter((s) => !selectedIds.includes(s.id));
        let filtered = response.skills;
        filtered = [...filtered].sort((a, b) => {
          const nameComparison = a.name.localeCompare(b.name);
          if (nameComparison === 0) {
            return a.sub_skill.localeCompare(b.sub_skill);
          }
          return nameComparison;
        });
        setMasterSkills(filtered);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const loadUsers = async (pageParam) => {
    try {
      let combinedParams: any = { ...filteredParams };
      if (collection && collection.id) {
        combinedParams.exclude_collection_id = collection.id;
      }


      let _users = await userService.getUsers(pageParam, perPage, combinedParams, sortOption);
      if (_users && _users.length > 0) {
        setPage((prev) => prev + 1);
        // const currentMemberUuids = currentMembers.map(m => m.uuid);
        // const newUsers = _users.filter(u => !currentMemberUuids.includes(u.uuid));
        const newUsers = _users;
        setFilteredUsers((prevRecords) => [...prevRecords, ...newUsers]);
        if (_users.length < perPage) {
          // This was the last page - we got fewer profiles than requested
          setNoMoreData('All Profiles Loaded');
        } else {
          setNoMoreData('');
        }
        return;
      }
      if (pageParam > 1) {
        setNoMoreData('All Profiles Loaded');
      } else if (filteredUsers.length <= 0) {
        setNoMoreData('No matching profiles.');
      } else {
        setNoMoreData('All Profiles Loaded');
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      console.log('loadUsers: setIsUserLoading false: ', isUserLoading);
      setIsUserLoading(false);
    }
  };

  const loadMore = async () => {
    if (isUserLoading) {
      console.log('sb: directory isLoading');
      return;
    }
    console.log('loadUsers: setIsUserLoading true: ', isUserLoading);
    setIsUserLoading(true);
  };
  useEffect(() => {
    if (isUserLoading) {
      console.log('sb::winodws isLoading');
      const load = async () => {
        await loadUsers(page);
      };
      load();
    }
  }, [isUserLoading]);
  useEffect(() => {
    if (!isUserLoading) {
      setNoMoreData('');
      setPage(1);
      setFilteredUsers([]);
      loadMore();
    }
  }, [filteredParams, sortOption]);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        console.log('sb::intersect:observerRef loadmore');
        loadMore();
      }
    }, {
      rootMargin: '0px 0px 50px 0px',
      threshold: 0
    });

    if (observerAddFavRef.current) {
      observer.observe(observerAddFavRef.current);
    }
    return () => {
      observer.disconnect()
    };
  }, [observerAddFavRef.current]);
  useEffect(() => {
    if (collection != null) {
      setcollectionSelected(collection)
    }
  }, [collection]);


  useEffect(() => {
    if (collectionSelected != null) {
      fetchGroupSharedUsers(collectionSelected.id)
      loadCollectionMembers();
      loadAllGroups();
      loadMasterSkills()
    }
  }, [collectionSelected]);

  useEffect(() => {
    // Reset and reload users when the Add to Collection modal opens
    if (showAddToCollectionModal) {
      setNoMoreData('');
      setPage(1);
      setFilteredUsers([]);
      setSelectedUsersToAdd([]);
      loadMore();
    }
  }, [showAddToCollectionModal]);

  const loadCollectionMembers = async () => {
    try {
      const response = await fetch(`/groups/${collectionSelected.id}/members`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMembers(data.members || []);
      setCurrentMembers(data.members || []);
    } catch (err) {
      console.error('Error fetching collection members:', err);
      setMembers([]);
      setCurrentMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupSharedUsers = async (groupId: number): Promise<User[]> => {
    try {
      const response = await fetch(`/groups/${groupId}/shared_users`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const users = data.users || [];
      const owner_user = data.owner_user || null;
      setCurrentlySharedWith(users);
      setcollectionOwner(owner_user)
    } catch (err) {
      console.error('Error fetching shared users:', err);
      return [];
    }
  };

  const loadAllGroups = async () => {
    try {
      const response = await fetch('/groups/groups_json', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAllGroups(data);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setAllGroups([]);
    }
  };

  const handleAddToGroup = async (userUuid: string, groupId: number) => {
    if (!isOwnerCheck()) {
      return;
    }
    if (isArchived()) {
      return;
    }
    setIsCreatingGroup(true);
    try {
      const response = await fetch(`/groups/${groupId}/add_members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ user_uuid: userUuid })
      });

      if (response.ok) {
        console.log('User added to collection successfully');
      } else {
        console.error('Failed to add user to collection');
      }
    } catch (error) {
      console.error('Error adding to group:', error);
    } finally {
      setIsCreatingGroup(false);
      setShowGroupMenu(null);
    }
  };

  const handleGroupIconClick = (userUuid: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isOwnerCheck()) {
      return;
    }
    setShowGroupMenu(showGroupMenu === userUuid ? null : userUuid);
  };

  const openCreateModal = (userUuid?: string) => {
    if (!isOwnerCheck()) {
      return;
    }
    setIsModalOpen(true);
  };

  // const fetchAvailableUsers = async (): Promise<User[]> => {
  //   try {
  //     const response = await fetch('/users/share_index', {
  //       headers: {
  //         'Accept': 'application/json',
  //         'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  //       },
  //       credentials: 'same-origin'
  //     });
  //     const data = await response.json();
  //     return (data.users || []).filter((user: User) => !currentUser || user.uuid !== currentUser.uuid);
  //   } catch (err) {
  //     console.error('Error fetching available users:', err);
  //     return [];
  //   }
  // };



  const handleOpenManageMembers = async () => {
    onShowShareModal();
    // if(showSharedCollection) {
    //   return;
    // }
    // if(isArchived()){
    //   return;
    // }
    // setManageMembersGroup(collectionSelected);
    // setSelectedMembers([]);
    // setSearchMember('');

    // const availableUsers = await fetchAvailableUsers();
    // const memberUuids = new Set(members.map(m => m.uuid));
    // const filteredUsers = availableUsers.filter(user =>
    //   !memberUuids.has(user.uuid) && (!currentUser || user.uuid !== currentUser.uuid)
    // );
    // setAvailableUsersForManage(filteredUsers);
  };

  const toggleMemberSelection = (uuid: string) => {
    if (!isOwnerCheck()) {
      return;
    }
    if (isArchived()) {
      return;
    }
    setSelectedMembers(prev =>
      prev.includes(uuid)
        ? prev.filter(id => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleAddMembers = async () => {
    if (!isOwnerCheck()) {
      return;
    }
    if (isArchived()) {
      return;
    }

    if (!manageMembersGroup || selectedMembers.length === 0) return;

    try {
      const response = await fetch(`/groups/${manageMembersGroup.id}/add_members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          member_ids: selectedMembers
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        await loadCollectionMembers();
        setSelectedMembers([]);
        setManageMembersGroup(null);
        // onAddToCollection(manageMembersGroup.id, selectedMembers);
      } else {
        console.error('Failed to add members:', result);
      }
    } catch (error) {
      console.error('Error adding members:', error);
    }
  };

  const handleRemoveMember = async (memberUuid: string) => {
    if (!isOwnerCheck()) {
      return;
    }
    if (isArchived()) {
      return;
    }
    if (!collectionSelected) return;

    try {
      const response = await fetch(`/groups/${collectionSelected.id}/remove_member/${memberUuid}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        await loadCollectionMembers();
        refreshCollection(true, true);
      } else {
        console.error('Failed to remove member:', result);
      }
    } catch (error) {
      console.error('Error removing member:', error);
    }
  };

  const handleArchive = async (groupId: number) => {
    try {
      if (!isOwnerCheck()) {
        return;
      }
      const response = await fetch(`/groups/${groupId}/archive`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (response.ok) {
        onBack(true); // Go back to directory after archiving
      } else {
        console.error('Failed to archive group');
      }
    } catch (error) {
      console.error('Error archiving group:', error);
    }
  };

  // Add to Collection Modal Functions
  // const getFilteredUsersForAdd = async () => {
  //   if (!isOwnerCheck()) {
  //     return;
  //   }

  //   let baseUsers = [...allAvailableUsers];
  //   if (selectedCollectionFilter === 'favorites') {
  //     baseUsers = baseUsers.filter(user => {
  //       return favorites.findIndex((item) => item.uuid == user.uuid) >= 0;
  //     });
  //   } else if (selectedCollectionFilter !== 'all' && typeof selectedCollectionFilter === 'number') {
  //     try {
  //       const members = await fetch(`/groups/${selectedCollectionFilter}/members`).then(r => r.json());
  //       const memberUuids = new Set(members.members?.map((m: User) => m.uuid) || []);
  //       baseUsers = baseUsers.filter(user => memberUuids.has(user.uuid));
  //     } catch (error) {
  //       console.error('Error fetching collection members:', error);
  //       baseUsers = [];
  //     }
  //   }

  //   const filtered = baseUsers.filter(user => {
  //     if (selectedCollectionFilter === 'all' &&
  //       // favorites.has(user.uuid)
  //       favorites.findIndex((item) => item.uuid == user.uuid) >= 0

  //     ) {
  //       return false;
  //     }

  //     if (addFavoritesSearchTerm) {
  //       const searchLower = addFavoritesSearchTerm.toLowerCase();
  //       const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
  //       if (!fullName.includes(searchLower)) return false;
  //     }

  //     if (addFavoritesFilters.gender && user.gender !== addFavoritesFilters.gender) {
  //       return false;
  //     }

  //     if (addFavoritesFilters.ethnicity) {
  //       if (!user.ethnicities?.includes(addFavoritesFilters.ethnicity)) {
  //         return false;
  //       }
  //     }

  //     if (addFavoritesFilters.country && user.country !== addFavoritesFilters.country) {
  //       return false;
  //     }

  //     if (user.weight < addFavoritesFilters.weightMin || user.weight > addFavoritesFilters.weightMax) {
  //       return false;
  //     }

  //     if (user.height < addFavoritesFilters.heightMin || user.height > addFavoritesFilters.heightMax) {
  //       return false;
  //     }

  //     return true;
  //   });

  //   return filtered;
  // };

  const toggleUserAddSelection = (uuid: string) => {
    setSelectedUsersToAdd(prev =>
      prev.includes(uuid)
        ? prev.filter(id => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleSelectedMembersToCollection = async () => {
    if (!isOwnerCheck()) {
      return;
    }
    if (selectedUsersToAdd.length === 0) return;

    try {
      const promises = selectedUsersToAdd.map(uuid =>
        fetch(`/groups/${collectionSelected.id}/add_members`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: JSON.stringify({ user_uuid: uuid })
        })
      );

      await Promise.all(promises);
      await loadCollectionMembers();
      setShowAddToCollectionModal(false);
      setSelectedUsersToAdd([]);
      refreshCollection(true, true);
      console.log(`${selectedUsersToAdd.length} member(s) added to collection!`);
    } catch (error) {
      console.error('Error adding members to collection:', error);
    }
  };

  const handleClearFilters = () => {
    setAddFavoritesFilters({
      gender: '',
      ethnicity: '',
      country: '',
      weightMin: measurementRanges.weight.min,
      weightMax: measurementRanges.weight.max,
      heightMin: measurementRanges.height.min,
      heightMax: measurementRanges.height.max,
    });
  };

  const sortedMembers = [...members].sort((a, b) => {
    switch (sortOption) {
      case 'newest':
        return b.id - a.id;
      case 'oldest':
        return a.id - b.id;
      case 'name-asc':
        return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      case 'name-desc':
        return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
      default:
        return 0;
    }
  });

  const isArchived = () => {
    if (collectionSelected == null) {
      return true;
    }
    return collectionSelected.archived === true || collectionSelected.status === 'Archived' ;
  }

  // Safe check for currentUser to prevent errors
  const isOwnerCheck = () => {
    if (!currentUser || !collectionSelected) return false;
    if (collectionSelected.isOwner) return true;
    if (collectionOwner && collectionOwner.uuid === currentUser.uuid) return true;
    return false;
  };

  const handleSaveDescription = async (groupId: number, description: string) => {
    try {
      const response = await fetch(`/groups/${groupId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          group: {
            description: description
          }
        })
      });
      console.log(response);
      if (response.ok) {
        const data = await response.json();
        if ('group' in data) {
          onSave(data.group);
        }
        collectionSelected.description = description;
        seteditingDescriptionId('');
        showNotification('Description updated successfully!', 'success');
      } else {
        console.error('Failed to update description');
        showNotification('Failed to update description', 'danger');
      }
    } catch (error) {
      console.error('Error updating description:', error);
      showNotification('Error updating description', 'danger');
    }
  };

  const handleSaveTitle = async (groupId: number, title: string) => {
    try {
      if (title.trim().length <= 0) {
        return;
      }
      const response = await fetch(`/groups/${groupId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          group: {
            title: title
          }
        })
      });
      console.log(response);
      if (response.ok) {
        const data = await response.json();
        if ('group' in data) {
          onSave(data.group);
        }
        collectionSelected.title = title;
        seteditingTitleId(null);
        showNotification('Title updated successfully!', 'success');
      } else {
        console.error('Failed to update Title');
        showNotification('Failed to update Title', 'danger');
      }
    } catch (error) {
      console.error('Error updating Title:', error);
      showNotification('Error updating Title', 'danger');
    }
  };

  const showNotification = (message: string, type: any) => {
    showToast(message, type);
  };
  const activeArchivedRender = () => {
    return <>
      {!isArchived() ? (
        <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" />
            <path d="M4 6L5.5 7.5L8 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Active
        </div>
      ) : (
        <div className="flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">
          <CircleOffIcon className="w-4 h-4 flex-shrink-0" />
          Archived
        </div>
      )}</>;
  }

  return (
    <div className="w-full flex flex-col flex-1 mx-auto bg-gray-50">
      <Breadcrumb
        pages={[
          { name: 'Member Directory', href: '/users/directory?tab=all', current: false },
          { name: 'Collections', href: '/users/directory?tab=collections', current: true },
        ]}
      ></Breadcrumb>

      {collectionSelected != null && collectionSelected.archived && !isOwnerCheck() ? <>
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8 h-full flex flex-row items-center"
          style={{
            height: '80vh'
          }}>
          <div className='flex flex-col justify-center items-center px-16 gap-2-1'>
            <div className='w-16 h-16'><EyeOffIcon className='w-16 h-16' /></div>
            <div className='font-semibold text-2xl text-gray-900'>Collection Access Removed</div>
            <div className='font-regular text-sm text-gray-400'>You've lost your viewer access to this Collection.</div>
          </div>
        </div>
      </> : <>
        {collectionSelected != null && <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-10">
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-0  items-start sm:items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    onBack(false);
                  }}
                  className="flex items-center justify-center rounded-full hover:bg-gray-100 flex-shrink-0"
                  style={{
                    width: '32px',
                    height: '32px',
                    padding: '0.5rem',
                    background: '#FFFFFF',
                    border: '1px solid #D1D5DB',
                    boxShadow: '0px 2px 4px -2px rgba(0, 0, 0, 0.12)',
                    borderRadius: '999px',
                  }}
                  title="Back to collections"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <div className="flex items-center gap-2">
                  {editingTitleId !== null ? (
                    <input
                      autoFocus
                      value={editingTitleId}
                      onChange={(e) => seteditingTitleId(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveTitle(collectionSelected.id, editingTitleId ?? '');
                        } else if (e.key === 'Escape') {
                          seteditingTitleId(null);
                        }
                      }}
                      onBlur={() => {
                        handleSaveTitle(collectionSelected.id, editingTitleId);
                      }}
                      className="w-full text-left border border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '150%',
                        color: '#0D1621',
                        resize: 'vertical'
                      }}
                    />
                  ) : (<h1
                    className="truncate"
                    style={{
                      fontFamily: 'Poppins',
                      fontWeight: 600,
                      fontSize: '24px',
                      lineHeight: '150%',
                      color: '#111827',
                    }}
                    onDoubleClick={() => {
                      if (isOwnerCheck() && !isArchived()) {
                        seteditingTitleId(collectionSelected.title);
                      }
                    }}
                  >
                    {collectionSelected.title}
                  </h1>)}
                  { isOwnerCheck() && !isArchived() && <div
                    onClick={() => {
                      seteditingTitleId(collectionSelected.title);
                    }}
                  ><PencilLineIcon className="h-4 w-4 text-gray-500 underline" /></div>
                  }

                  <span className="text-gray-600 text-sm">({sortedMembers.length})</span>
                  <span className="hidden sm:flex ">{activeArchivedRender()}</span>
                </div>
              </div>

              {isOwnerCheck() &&
                <div className="flex items-center gap-2">
                  {/* Hide these buttons on mobile, show on tablet+ */}
                  {isOwnerCheck() && <>
                    <button
                      onClick={() => setShowAddToCollectionModal(true)}
                      className={"hidden sm:flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" +
                        (isArchived() ? ' opacity-30 ' : ' ')
                      }
                      disabled={isArchived()}
                      style={{
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '14px',
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 3.5V12.5M3.5 8H12.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      Add to Collection
                    </button>

                    <button
                      onClick={handleOpenManageMembers}
                      className={"hidden sm:flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" +
                        (isArchived() ? ' opacity-30 ' : ' ')
                      }
                      disabled={isArchived()}
                      style={{
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '14px',
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 8H2.5M8 2.5V13.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      Share Access
                    </button>
                  </>}
                  <span className="flex sm:hidden ">{activeArchivedRender()}</span>
                  {/* Mobile: Show only menu button */}
                  {isOwnerCheck() && <div className="relative sm:hidden">
                    <button
                      onClick={() => setShowMobileMenu(!showMobileMenu)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8H3.00667M8 8H8.00667M13 8H13.0067" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>

                    {showMobileMenu && isOwnerCheck() && (
                      <div className="absolute left-0 sm:right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        {isOwnerCheck() && <><button
                          onClick={() => {
                            setShowAddToCollectionModal(true);
                            setShowMobileMenu(false);
                          }}
                          disabled={isArchived()}
                          className={"w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" +
                            (isArchived() ? ' opacity-30 ' : ' ')
                          }
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3.5V12.5M3.5 8H12.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                          Add to Collection
                        </button>
                          <button
                            onClick={() => {
                              handleOpenManageMembers();
                              setShowMobileMenu(false);
                            }}
                            className={"w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" +
                              (isArchived() ? ' opacity-30 ' : ' ')
                            }
                            disabled={isArchived()}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M13.5 8H2.5M8 2.5V13.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            Share Access
                          </button>

                          <button
                            onClick={() => {
                              handleArchive(collectionSelected.id);
                              setShowMobileMenu(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                            Archive Collection
                          </button>
                        </>
                        }
                      </div>
                    )}
                  </div>
                  }

                  {/* Desktop: Show menu button */}
                  {/* <div className="relative hidden sm:block">
                <button className="p-2 hover:bg-gray-100 rounded-lg">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H3.00667M8 8H8.00667M13 8H13.0067" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div> */}
                  <div className="relative">
                    <button
                      data-sort-button
                      onClick={() => setShowSortMenu(!showSortMenu)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      title="Sort members"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                      </svg>
                    </button>

                    {showSortMenu && (
                      <div
                        data-sort-menu
                        className="absolute sm:right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                      >
                        <button
                          onClick={() => {
                            setSortOption('newest');
                            setShowSortMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'newest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                            }`}
                        >
                          New Members First
                        </button>
                        <button
                          onClick={() => {
                            setSortOption('oldest');
                            setShowSortMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'oldest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                            }`}
                        >
                          Old Members First
                        </button>
                        <button
                          onClick={() => {
                            setSortOption('name-asc');
                            setShowSortMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'name-asc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                            }`}
                        >
                          Name (A-Z)
                        </button>
                        <button
                          onClick={() => {
                            setSortOption('name-desc');
                            setShowSortMenu(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'name-desc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                            }`}
                        >
                          Name (Z-A)
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              }
            </div>
            {/* archived  Row */}
            {isArchived() && <div className="flex items-center justify-center  bg-white border border-gray-200">
              <div className="flex items-center justify-center  p-8 gap-5">
                <div className="flex items-center justify-center  p-8 gap-5 text-gray-400">
                  <CircleOffIcon className='w-16 h-16' />
                </div>
                <div className="flex flex-col items-start justify-start border-l border-gray-400 pl-5 gap-2-1">
                  <div className='font-semibold text-gray-900' style={{
                    fontSize: '1.5rem'
                  }}>
                    Collection Archived
                  </div>
                  <div className='font-regular text-gray-400 text-sm' style={{
                  }}>
                    Access and editing are paused since this collection is archived. Unarchive to restore Active status.
                  </div>
                </div>
              </div>
            </div>}
            {/* Description + Viewers Row */}
            {(collectionSelected.description || sortedMembers.length > 0) && (
              <div className={"mb-6 flex flex-col sm:flex-row sm:justify-between " +
                (isArchived() ? ' opacity-30 ' : ' ')
              }
              >
                <div className="gap-5 flex flex-col pr-4   ">
                  {collectionSelected.description && (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-bold">Description</span>
                          {isOwnerCheck() && !isArchived() && <div
                            onClick={() => {
                              seteditingDescriptionId(collectionSelected.description);
                            }}
                          ><PencilLineIcon className="h-4 w-4 text-gray-500 underline" /></div>}
                        </div>

                        { collectionSelected.description.length > 140 && <button
                          onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                          className="flex md:hidden  items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={`transform transition-transform ${isDescriptionExpanded ? 'rotate-180' : ''}`}>
                            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span className="text-xs">{isDescriptionExpanded ? 'Collapse' : 'View more'}</span>
                        </button> }
                      </div>

                      {editingDescriptionId !== '' ? (
                        <textarea
                          autoFocus
                          value={editingDescriptionId}
                          onChange={(e) => seteditingDescriptionId(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveDescription(collectionSelected.id, editingDescriptionId ?? '');
                            } else if (e.key === 'Escape') {
                              seteditingDescriptionId('');
                            }
                          }}
                          onBlur={() => {
                            handleSaveDescription(collectionSelected.id, editingDescriptionId);
                          }}
                          className="w-full text-left border border-blue-500 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '150%',
                            color: '#0D1621',
                            minHeight: '120px',
                            resize: 'vertical'
                          }}
                          rows={4}
                        />
                      ) : (
                        <p
                          className={`text-gray-600 mb-3 ${!isDescriptionExpanded ? 'line-clamp-3 lg:line-clamp-none' : ''}`}
                          style={{
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '150%',
                          }}
                          onDoubleClick={() => {
                            if (isOwnerCheck() && !isArchived()) {
                              seteditingDescriptionId(collectionSelected.description);
                            }
                          }}

                        >
                          {collectionSelected.description}
                        </p>)}

                    </>
                  )}
                  {collectionSelected.hashtag && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {collectionSelected.hashtag.split('#').map((tag, index) => {
                        if (tag.trim().length <= 0) {
                          return <></>;
                        }
                        return (<><div
                          key={index}
                          className="flex items-center rounded-full px-3 border border-gray-300"
                        >
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2">
                              <div className=" overflow-hidden whitespace-nowrap text-overflow: ellipsis max-w-xs">
                                <span className="text-xs font-medium leading-5 text-gray-700">{tag}</span>
                              </div>

                            </div>
                          </div>
                          <div>
                            <XIcon className='w-4 h-4' />
                          </div>
                        </div></>);
                      })}
                    </div>
                  )}
                </div>

                {/* Viewers Section - Show on Mobile when expanded, always show on Desktop */}
                {currentlySharedWith.length > 0 && (
                  <div className={`gap-5 flex flex-col sm:pl-8 pr-4 sm:border-l`}
                    style={{ minWidth: '20rem' }}>
                    {isOwnerCheck() && <>
                      <h3 className="text-base font-bold">Viewers</h3>
                      <div className="flex -space-x-2 cursor-pointer" >
                        {currentlySharedWith.slice(0, 15).map((user, index) => (
                          <div
                            key={index}
                            className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium overflow-hidden hover:border-blue-300 transition-colors"
                          >
                            <UserIcon imageUrl={user.croppedAvatarUrl || defaultProjectPlaceHolder} name={`${user.firstName} ${user.lastName}`} index={index} />
                          </div>

                          // <img
                          //   key={user.uuid}
                          //   src={user.croppedAvatarUrl || defaultProjectPlaceHolder}
                          //   onError={(e) => {
                          //     e.currentTarget.src = defaultProjectPlaceHolder;
                          //   }}
                          //   alt={`${user.firstName} ${user.lastName}`}
                          //   className="w-8 h-8 rounded-full border-2 border-white object-cover"
                          // />
                        ))}
                        {currentlySharedWith.length > 15 && (
                          <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                            +{currentlySharedWith.length - 15}
                          </div>
                        )}
                      </div>
                    </>}
                    {true && !isOwnerCheck() && collectionOwner != null && <>
                      <h3 className="text-base font-bold">Owner</h3>
                      <div className="flex -space-x-2 cursor-pointer" >
                        <div
                          className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium overflow-hidden hover:border-blue-300 transition-colors"
                        >
                          <UserIcon imageUrl={collectionOwner.croppedAvatarUrl || defaultProjectPlaceHolder} name={`${collectionOwner.firstName} ${collectionOwner.lastName}`} index={0} />
                        </div>
                        {/* show owner image */}
                      </div>
                    </>}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Members Header with Sort */}
          <div className={"flex items-center justify-between mb-4" +
            (isArchived() ? ' opacity-30 ' : ' ')
          }>
            <h2 className="text-lg font-semibold text-gray-900">
              Members ({sortedMembers.length})
            </h2>
          </div>

          {/* Members Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <svg className="animate-spin h-8 w-8 mx-auto text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-gray-500">Loading members...</p>
              </div>
            </div>
          ) : sortedMembers.length === 0 ? (
            <div className="text-center py-12 sm:py-16 px-4">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-3 sm:mb-4" width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M48.0007 56C48.0007 50.342 45.753 44.9158 41.7523 40.9151C37.7515 36.9143 32.3253 34.6667 26.6673 34.6667M26.6673 34.6667C21.0094 34.6667 15.5831 36.9143 11.5824 40.9151C7.5816 44.9158 5.33398 50.342 5.33398 56M26.6673 34.6667C34.0311 34.6667 40.0007 28.6971 40.0007 21.3333C40.0007 13.9695 34.0311 8 26.6673 8C19.3035 8 13.334 13.9695 13.334 21.3333C13.334 28.6971 19.3035 34.6667 26.6673 34.6667ZM58.6675 53.3339C58.6675 44.3472 53.3341 36.0005 48.0008 32.0005C49.7539 30.6852 51.1558 28.9581 52.0823 26.9719C53.0088 24.9857 53.4315 22.8018 53.3128 20.6133C53.1942 18.4249 52.5379 16.2994 51.4021 14.4251C50.2662 12.5507 48.6858 10.9852 46.8008 9.86719" stroke="#C3CBDA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>

              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No members yet</h3>
              <p className="text-sm sm:text-base text-gray-500">This collection doesn't have any members yet</p>
            </div>
          ) : (
            <div
              className={
                (!isMobile
                  ? ' grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-2.5 justify-items-center items-start '
                  : ' grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 gap-3 sm:gap-4 justify-items-center items-start px-2') +
                (isArchived() ? ' opacity-30 ' : ' ')
              }
            >
              {sortedMembers.map((user) => (
                // <div
                //   key={user.uuid}
                //   className="overflow-hidden relative flex flex-1 flex-col bg-white rounded-xl border border-gray-300 p-4 shadow-md"
                // >
                <div
                  key={user.uuid}
                  className="overflow-hidden relative w-full flex flex-col bg-white rounded-xl border border-gray-300 shadow-md"
                  style={{
                    padding: '0px',
                    boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                  }}
                >
                  <UserItemUi currentUser={currentUser} user={user}
                  collectionSelected={collectionSelected} 
                  isArchived={isArchived()}
                  isOwner={isOwnerCheck()}
                  onRemove={async function (user: User, collectionSelected: Group): Promise<void> {
                    // syca_check
                    await handleRemoveMember(user.uuid);
                  } } />

                  {/* Name and Actions Container */}
                  <div
                    className="flex items-center bg-white"
                    style={{
                      padding: '1rem  12px',
                      width: '100%',
                      height: '3.188rem',
                      flexGrow: 0,
                    }}
                  >
                    <a
                      href={`/users/${user.uuid}?from=collection&collection_id=${collectionSelected.id}`}
                      className="relative flex items-center text-left flex-1"
                      style={{ minWidth: 0 }}
                      onClick={(e) => {
                        e.preventDefault();
                        const target = e.currentTarget.closest('.rounded-xl')?.querySelector('div[a-href]') as HTMLElement;
                        if (target) target.click();
                      }}
                    >
                      <p
                        className="flex items-center text-left"
                        style={{
                          maxWidth: '8.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',

                          fontWeight: 600,
                          fontSize: '1rem',
                          lineHeight: '120%',
                          letterSpacing: '-0.25px',
                          color: '#0D1621',
                          margin: 0,
                        }}
                      >
                        {user.firstName} {user.lastName || ''}
                      </p>
                      <div className="absolute top-0 bottom-0 right-0 w-12 bg-gradient-to-l from-white pointer-events-none"></div>
                    </a>

                    {isOwnerCheck() && (
                      <div className="flex items-center gap-2 ml-auto">
                        {/* Favorite toggle (heart) */}
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isArchived()) {
                              await onToggleFavorite(user.uuid, e, user);
                              //  remove from collection list also. syca_check
                              await loadCollectionMembers();
                              refreshCollection(true, false);

                            }
                          }}
                          className="flex items-center justify-center transition-all hover:scale-105"
                          style={{
                            width: '1.75rem',
                            height: '1.75rem',
                            padding: 0,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            zIndex: 10
                          }}
                          aria-label={
                            favoriteUsers.findIndex((item) => item.uuid == user.uuid) >= 0
                              ? 'Remove from favorites' : 'Add to favorites'
                          }
                        >
                          {
                            favoriteUsers.findIndex((item) => item.uuid == user.uuid) >= 0
                              ? (
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M5.66669 11.9167C5.6667 10.9893 5.94801 10.0838 6.47346 9.31973C6.99891 8.55563 7.74378 7.96889 8.60969 7.63701C9.4756 7.30513 10.4218 7.24372 11.3234 7.46088C12.2249 7.67805 13.0394 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8143 8.96268C13.8729 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1272 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3409 8.85335C14.9587 8.1591 15.7734 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3958 7.62292C20.2636 7.95553 21.0095 8.54456 21.5343 9.3116C22.0591 10.0786 22.3378 10.9873 22.3334 11.9167C22.3334 13.825 21.0834 15.25 19.8334 16.5L15.2567 20.9275C15.1014 21.1059 14.91 21.2491 14.6951 21.3478C14.4802 21.4464 14.2467 21.4982 14.0103 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1075 21.2605 12.9142 21.1197 12.7567 20.9433L8.16669 16.5C6.91669 15.25 5.66669 13.8333 5.66669 11.9167Z"
                                    fill="#8F79F1"
                                    stroke="#8570E4"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : (
                                // outline heart
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M5.66666 11.9167C5.66667 10.9893 5.94798 10.0838 6.47343 9.31973C6.99888 8.55563 7.74375 7.96889 8.60966 7.63701C9.47557 7.30513 10.4218 7.24372 11.3233 7.46088C12.2249 7.67805 13.0393 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8142 8.96268C13.8728 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1271 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3408 8.85335C14.9587 8.1591 15.7733 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3957 7.62292C20.2635 7.95553 21.0095 8.54456 21.5343 9.3116C22.059 10.0786 22.3378 10.9873 22.3333 11.9167C22.3333 13.825 21.0833 15.25 19.8333 16.5L15.2567 20.9275C15.1014 21.1059 14.9099 21.2491 14.6950 21.3478C14.4801 21.4464 14.2467 21.4982 14.0102 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1074 21.2605 12.9142 21.1197 12.7567 20.9433L8.16666 16.5C6.91666 15.25 5.66666 13.8333 5.66666 11.9167Z"
                                    stroke="#374151"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                        </button>

                        {/* Collection / add-to-group icon */}
                        <button
                          data-group-button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isArchived()) {
                              handleGroupIconClick(user.uuid, e);
                            }
                          }}
                          className="flex items-center justify-center transition-all hover:scale-105"
                          style={{
                            width: '1.75rem',
                            height: '1.75rem',
                            padding: 0,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            zIndex: 10
                          }}
                          aria-label="Add to collection"
                        >
                           <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M15.8334 17.5L10 14.1667L4.16669 17.5V4.16667C4.16669 3.72464 4.34228 3.30072 4.65484 2.98816C4.9674 2.67559 5.39133 2.5 5.83335 2.5H14.1667C14.6087 2.5 15.0326 2.67559 15.3452 2.98816C15.6578 3.30072 15.8334 3.72464 15.8334 4.16667V17.5Z"
                                      fill="#8F79F1"
                                      stroke="#8570E4"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                          {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scale(1.0)' }}>
                            <path
                              d="M15.8334 17.5L10 14.1667L4.16669 17.5V4.16667C4.16669 3.72464 4.34228 3.30072 4.65484 2.98816C4.9674 2.67559 5.39133 2.5 5.83335 2.5H14.1667C14.6087 2.5 15.0326 2.67559 15.3452 2.98816C15.6578 3.30072 15.8334 3.72464 15.8334 4.16667V17.5Z"
                              stroke="#374151"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg> */}
                        </button>
                      </div>
                    )}
                  </div>


                  {showGroupMenu === user.uuid && (
                    <div
                      data-group-menu
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-50 w-64 bg-white rounded-lg shadow-lg border border-gray-200"
                      style={{ top: '100%', right: 0, marginTop: '0.5rem' }}
                    >
                      <GroupContextMenu
                        user={user}
                        groups={allGroups}
                        onAddToGroup={(groupId) => handleAddToGroup(user.uuid, groupId)}
                        onOpenCreateModal={() => openCreateModal(user.uuid)}
                        onClose={() => setShowGroupMenu(null)}
                        isCreating={isCreatingGroup}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>}
      </>}

      {/* Add to Collection Modal */}
      {
        showAddToCollectionModal && (
          <div className="fixed inset-0 bg-customGray flex items-center justify-center z-50 px-2 pb-2 sm:px-4 sm:pb-4 pt-20"
            //style={{ backgroundColor: '#6b7a8a' }} 
            onClick={() => setShowAddToCollectionModal(false)}>
            <div className="bg-white rounded-xl shadow-xl flex flex-col overflow-hidden w-full h-full"
              style={{
                width: '100%',
                height: '100%', maxWidth: '73.125rem', maxHeight: '90vh',
                ...(window.innerWidth >= 1024 && {
                  width: '73.125rem',
                  height: '90vh'
                })
              }}
              onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Add Members to {collectionSelected.title}
                </h2>
                <button
                  onClick={() => setShowAddToCollectionModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mobile Filter Toggle */}
              {/* <div className="lg:hidden border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                  className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <span>Filters</span>
                  <svg
                    className={`w-5 h-5 transform transition-transform ${isMobileFiltersOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div> */}

              {/* Main Content */}
              <div className="flex flex-1 overflow-hidden">
                {/* Left Sidebar - Filters - Desktop */}
                <div className="hidden lg:block w-80 border-r border-gray-200 p-6 overflow-y-auto flex-shrink-0 relative z-20">
                  <div className="w-64  mx-auto flex max-w-7xl items-left justify-between">
                    <div className="space-y-4">

                      <h3 className="text-sm font-medium text-gray-900 mb-3">Search Profiles:</h3>
                      <UserFilter
                        forceInline={true}
                        users={[]}
                        currentUser={currentUser}
                        measurementRanges={measurementRanges}
                        onFilter={(filteredUsers, filterParams: any) => {
                          setfilteredParams(filterParams)
                        }}
                        favorites={new Set(favoriteUsers.map(f => f.uuid))}
                        userGroupsAll={[...allGroups.filter(item => item.isOwner !== false).map((item) => {
                          return { id: item.id, name: item.title };
                        })]}
                        firstnameLastNameshow={false}
                        masterSkills={masterSkills}
                        keywordshow={true}
                        userGroupshow={true}
                        initialFavCollection={0}
                      />
                    </div>
                  </div>
                </div>


                {/* Mobile Filters Overlay */}
                {isMobileFiltersOpen && (
                  <>
                    <div
                      className="lg:hidden fixed inset-0 bg-customGray z-40"
                      // style={{ backgroundColor: '#6b7a8a' }}
                      onClick={() => setIsMobileFiltersOpen(false)}
                    />
                    <div className="lg:hidden fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white shadow-xl z-50 overflow-y-auto">
                      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                        <button
                          onClick={() => setIsMobileFiltersOpen(false)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="p-4 space-y-4">

                        <h3 className="text-sm font-medium text-gray-900 mb-3">Search Profiles:</h3>
                        <UserFilter
                          forceInline={true}
                          openFilters={isMobileFiltersOpen}
                          firstnameLastNameshow={false}
                          keywordshow={true}
                          userGroupshow={true}
                          userGroupsAll={[...allGroups.filter(item => item.isOwner !== false).map((item) => {
                            return { id: item.id, name: item.title };
                          })]}
                          users={[]}
                          measurementRanges={{
                            height: {
                              min: 91, // 3ft : 91.44cm
                              max: 244, // 8ft: 243.84cm
                            },
                            weight: {
                              min: 22, // 50:lbs = 22.6796kg
                              max: 182, // 400:lbs = 181.437kg
                            },
                          }}
                          currentUser={currentUser}
                          onFilter={(filteredUsers, filterParams: any) => {
                            // setFilteredUsers()
                            setfilteredParams(filterParams)
                          }}
                          showFavoritesOnly={false}
                          favorites={new Set(favoriteUsers.map(f => f.uuid))}
                          initialFavCollection={0}
                          onClose={function (): void {
                            setIsMobileFiltersOpen(false);
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Right Side - Results */}
                <div className="flex-1 flex flex-col overflow-hidden relative z-10">
                  {/* Results Header */}
                  <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                      Results
                    </h3>
                    <div className="flex items-center gap-2">
                      {/* Mobile Filter Button */}
                      <button
                        onClick={() => setIsMobileFiltersOpen(true)}
                        className="lg:hidden p-2 hover:bg-gray-100 rounded"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setShowAddCollectSortMenu(!showAddCollectSortMenu)}
                          className="p-2 border border-gray-300 hover:bg-gray-100 rounded-lg flex items-center justify-center"
                          style={{ width: '2.25rem', height: '2.25rem' }}
                          title="Sort members"
                        >
                          <ArrowDownNarrowWide className="w-5 h-5 text-gray-600" />
                        </button>

                        {showAddCollectSortMenu && (
                          <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                            <button
                              onClick={() => {
                                setSortOption('newest');
                                setShowAddCollectSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'newest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              New Members First
                            </button>
                            <button
                              onClick={() => {
                                setSortOption('oldest');
                                setShowAddCollectSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'oldest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Old Members First
                            </button>
                            <button
                              onClick={() => {
                                setSortOption('name-asc');
                                setShowAddCollectSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'name-asc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Name (A-Z)
                            </button>
                            <button
                              onClick={() => {
                                setSortOption('name-desc');
                                setShowAddCollectSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'name-desc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Name (Z-A)
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Results Grid - Scrollable */}
                  <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                    {isLoadingFilteredUsers ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <svg className="animate-spin h-8 w-8 mx-auto text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className="text-sm text-gray-500">Loading members...</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 justify-items-center">
                          {filteredUsers.map((user) => {
                            const isSelected = selectedUsersToAdd.includes(user.uuid);
                            return (
                              <div
                                key={user.uuid}
                                onClick={() => toggleUserAddSelection(user.uuid)}
                                className={`flex flex-1 w-full flex-col bg-white rounded-xl border border-gray-300 p-4  ${isSelected ? 'ring-2 ring-purple-500 shadow-md ' : ' shadow-md '} `}
                                // className={`relative rounded-3xl overflow-hidden cursor-pointer transition-all bg-white ${isSelected ? 'ring-2 ring-purple-500 shadow-lg' : 'hover:shadow-md'}`}
                                style={{
                                  // height: '260px',
                                  minWidth: '11rem'
                                  // boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                                }}>
                                <div className="flex flex-1 flex-col h-full justify-between">
                                  {/* image */}
                                  <div className="flex flex-col w-full relative rounded-lg" style={{
                                    aspectRatio: '4/5',
                                    overflow: 'hidden',
                                  }}>
                                    <FilePreviewer file_url={user.croppedAvatarUrl} file_name={''}
                                      default_file_url={defaultProjectPlaceHolder}
                                      file_content_type={'image/png'}
                                      dynamicHeight={false}
                                      object_fit={'object-cover'}
                                      showControl={false}
                                    />
                                    {isSelected && (
                                      <div className="absolute top-2 right-2 bg-purple-600 rounded-full p-1.5 z-10">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                      </div>
                                    )}
                                    {/* {user.subscription_status === 'active' && (user.subscription_tier === 'premium'
                                  || user.subscription_tier === 'pro') && (
                                    <div className="absolute top-2 left-2 bg-black text-white text-xs font-semibold px-2 py-1 rounded z-10">
                                      PRO
                                    </div>
                                  )} */}
                                  </div>
                                  {/* <div
                                  className="relative bg-gradient-to-br"
                                  style={{
                                    padding: '12px 12px 0px',
                                    height: '200px',
                                    borderRadius: '10px 10px 0px 0px',
                                  }}
                                >
                                  <div className="relative w-full h-full" style={{ borderRadius: '0.5rem' }}>
                                    {user.subscription_status === 'active' && (user.subscription_tier === 'premium' || user.subscription_tier === 'pro') && (
                                      <div className="absolute top-2 left-2 bg-black text-white text-xs font-semibold px-2 py-1 rounded z-10">
                                        PRO
                                      </div>
                                    )}
                                    <Image
                                      src={user.croppedAvatarUrl}
                                      fallbackSrc={defaultProjectPlaceHolder}
                                      alt={`${user.firstName} ${user.lastName}`}
                                      className="w-full h-full object-cover rounded-lg"
                                    />
                                    {isSelected && (
                                      <div className="absolute top-2 right-2 bg-purple-600 rounded-full p-1.5 z-10">
                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                </div> */}

                                  {/* name and fav/collection icon */}
                                  <div
                                    className="flex items-center bg-white"
                                    style={{
                                      padding: '1rem  0.75rem',
                                      width: '100%',
                                      height: '3.188rem',
                                    }}
                                  >
                                    <p
                                      className="flex items-center text-left flex-1"
                                      style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',

                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        lineHeight: '120%',
                                        letterSpacing: '-0.25px',
                                        color: '#0D1621',
                                        margin: 0,
                                        marginRight: '0.25rem'
                                      }}
                                      title={`${user.firstName} ${user.lastName}`}
                                    >
                                      {(() => {
                                        const fullName = `${user.firstName || ''} ${user.lastName || ''}`;
                                        const shortName = `${user.firstName || ''} ${(user.lastName || '').charAt(0)}.`;
                                        return fullName.length * 9 > 140 ? shortName : fullName;
                                      })()}
                                    </p>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleUserAddSelection(user.uuid);
                                      }}
                                      className="flex-shrink-0"
                                    >
                                      {favoriteUsers.findIndex((item) => item.uuid == user.uuid) >= 0 ? (
                                        <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                                          <path
                                            d="M5.66669 11.9167C5.6667 10.9893 5.94801 10.0838 6.47346 9.31973C6.99891 8.55563 7.74378 7.96889 8.60969 7.63701C9.4756 7.30513 10.4218 7.24372 11.3234 7.46088C12.2249 7.67805 13.0394 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8143 8.96268C13.8729 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1272 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3409 8.85335C14.9587 8.1591 15.7734 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3958 7.62292C20.2636 7.95553 21.0095 8.54456 21.5343 9.3116C22.0591 10.0786 22.3378 10.9873 22.3334 11.9167C22.3334 13.825 21.0834 15.25 19.8334 16.5L15.2567 20.9275C15.1014 21.1059 14.91 21.2491 14.6951 21.3478C14.4802 21.4464 14.2467 21.4982 14.0103 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1075 21.2605 12.9142 21.1197 12.7567 20.9433L8.16669 16.5C6.91669 15.25 5.66669 13.8333 5.66669 11.9167Z"
                                            fill="#8F79F1"
                                            stroke="#8570E4"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      ) : (
                                        <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                                          <g filter="url(#filter0_d_7788_1309)">
                                            <path
                                              d="M5.66666 11.9167C5.66667 10.9893 5.94798 10.0838 6.47343 9.31973C6.99888 8.55563 7.74375 7.96889 8.60966 7.63701C9.47557 7.30513 10.4218 7.24372 11.3233 7.46088C12.2249 7.67805 13.0393 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8142 8.96268C13.8728 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1271 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3408 8.85335C14.9587 8.1591 15.7733 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3957 7.62292C20.2635 7.95553 21.0095 8.54456 21.5343 9.3116C22.059 10.0786 22.3378 10.9873 22.3333 11.9167C22.3333 13.825 21.0833 15.25 19.8333 16.5L15.2567 20.9275C15.1014 21.1059 14.9099 21.2491 14.695 21.3478C14.4801 21.4464 14.2467 21.4982 14.0102 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1074 21.2605 12.9142 21.1197 12.7567 20.9433L8.16666 16.5C6.91666 15.25 5.66666 13.8333 5.66666 11.9167Z"
                                              stroke="#374151"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </g>
                                          <defs>
                                            <filter id="filter0_d_7788_1309" x="-2" y="0" width="32" height="32" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                              <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                              <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect1_dropShadow_7788_1309" />
                                              <feOffset dy="2" />
                                              <feGaussianBlur stdDeviation="2" />
                                              <feComposite in2="hardAlpha" operator="out" />
                                              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                                              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_7788_1309" />
                                              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_7788_1309" result="shape" />
                                            </filter>
                                          </defs>
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div id='load_more_id' className="flex items-center flex-col py-4" ref={observerAddFavRef} >
                          {noMoreData.length > 0 && filteredUsers.length > 0 && (
                            <div className="font-bold text-xl">All Profiles Loaded</div>
                          )}
                          {noMoreData.length <= 0 && !isUserLoading && filteredUsers.length > 0 && (
                            <Button
                              id="profiles_search_loadmore_btn"
                              variant="secondary"
                              btn_size="small"
                              className="cursor-pointer"
                              onClick={() => {
                                loadMore();
                              }}
                            >
                              <div className="w-6 h-6  flex items-center justify-center">
                                <RefreshCcwIcon className="h-6 w-6" aria-hidden="true" />
                              </div>
                              Load More
                            </Button>
                          )}
                        </div>
                      </>
                    )}

                    {!isLoadingFilteredUsers && filteredUsers.length === 0 && (
                      <div className="text-center py-8 sm:py-12">
                        <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-gray-500 text-sm sm:text-base">No members found matching your filters</p>
                        <button
                          onClick={handleClearFilters}
                          className="mt-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          Clear all filters
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 bg-gray-50 flex-shrink-0">
                {/* Info Section */}
                <div className="p-3 sm:p-4 border-b border-gray-200">
                  <p className="text-xs sm:text-sm text-gray-600 text-center">
                    Items added here will be included in <strong>{collectionSelected.title}</strong>.
                  </p>
                </div>

                {/* Actions Section */}
                {selectedUsersToAdd.length > 0 && (
                  <div className="p-3 sm:p-4 bg-white flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      {selectedUsersToAdd.length} member{selectedUsersToAdd.length !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={handleSelectedMembersToCollection}
                      className="px-4 sm:px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors text-sm sm:text-base"
                    >
                      Add to Collection
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Manage Members Modal */}
      {
        manageMembersGroup && (
          <div
            className="fixed inset-0 bg-customGray flex items-center justify-center z-50 p-2 sm:p-4"
            // style={{ backgroundColor: '#6b7a8a' }}
            onClick={() => {
              setManageMembersGroup(null);
              setManageMembersActiveTab('add');
              setSelectedMembers([]);
            }}
          >
            {/* Mobile View (below 768px) - Tabbed Interface */}
            <div className="md:hidden w-full h-full flex items-center justify-center">
              <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto flex flex-col overflow-hidden"
                style={{ maxHeight: '90vh', height: '90vh' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Mobile Header */}
                <div className="flex items-start justify-between p-4 border-b border-gray-200 flex-shrink-0">
                  <div className="flex-1 pr-3">
                    <h2 className="text-base font-semibold text-gray-900 leading-tight">
                      Manage Members for {manageMembersGroup.title} collection
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      setManageMembersGroup(null);
                      setManageMembersActiveTab('add');
                      setSelectedMembers([]);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Mobile Tabs */}
                {manageMembersGroup.isOwner && (
                  <div className="flex border-b border-gray-200 flex-shrink-0">
                    <button
                      onClick={() => setManageMembersActiveTab('add')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${manageMembersActiveTab === 'add'
                        ? 'text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Invite as Viewers
                      {manageMembersActiveTab === 'add' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => setManageMembersActiveTab('current')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${manageMembersActiveTab === 'current'
                        ? 'text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Current Access
                      {manageMembersActiveTab === 'current' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                      )}
                    </button>
                  </div>
                )}

                {/* Mobile Tab Content */}
                <div className="flex-1 overflow-hidden flex flex-col">
                  {manageMembersActiveTab === 'add' && manageMembersGroup.isOwner ? (
                    // Mobile - Invite as Viewers Tab
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {/* Scrollable Content Area */}
                      <div className="flex-1 overflow-y-auto">
                        <div className="p-4">
                          <h3 className="text-base font-semibold text-gray-900 mb-3">
                            Invite as Viewers
                          </h3>

                          {/* Search */}
                          <div className="mb-3">
                            <input
                              type="text"
                              placeholder="Search Filmik Users"
                              value={searchMember}
                              onChange={(e) => setSearchMember(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                            />
                          </div>

                          {/* Available Members List */}
                          <div className="space-y-2">
                            {availableUsersForManage
                              .filter((user) =>
                                `${user.firstName} ${user.lastName}`
                                  .toLowerCase()
                                  .includes(searchMember.toLowerCase())
                              )
                              .map((user) => {
                                const isSelected = selectedMembers.includes(user.uuid);

                                return (
                                  <div
                                    key={user.uuid}
                                    onClick={() => toggleMemberSelection(user.uuid)}
                                    className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-all ${isSelected
                                      ? 'bg-blue-50 border-blue-300'
                                      : 'border-gray-200 hover:bg-gray-50'
                                      }`}
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <img
                                        src={user.croppedAvatarUrl || defaultProjectPlaceHolder}
                                        alt={user.firstName}
                                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {user.firstName} {user.lastName}
                                        </p>
                                      </div>
                                    </div>

                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-2 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                      }`}>
                                      {isSelected && (
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            {availableUsersForManage.filter((user) =>
                              `${user.firstName} ${user.lastName}`
                                .toLowerCase()
                                .includes(searchMember.toLowerCase())
                            ).length === 0 && (
                                <div className="text-center py-6 text-gray-500">
                                  <p className="text-sm">No available members found</p>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>

                      {/* Fixed Add Users Button - Always Visible */}
                      {selectedMembers.length > 0 && (
                        <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
                          <button
                            onClick={handleAddMembers}
                            className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add {selectedMembers.length} User{selectedMembers.length !== 1 ? 's' : ''}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Mobile - Current Access Tab
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {/* Scrollable Content Area */}
                      <div className="flex-1 overflow-y-auto">
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-semibold text-gray-900">
                              Current Access
                            </h3>
                            <span className="text-sm text-gray-500">
                              {currentMembers.length} viewer{currentMembers.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {/* Current Members List */}
                          <div className="space-y-2">
                            {currentMembers.length === 0 ? (
                              <div className="text-center py-8 text-gray-500">
                                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <p className="text-sm">No members in this collection yet</p>
                              </div>
                            ) : (
                              currentMembers.map((user) => {
                                const isOwner = user.uuid === currentUser.uuid;

                                return (
                                  <div
                                    key={user.uuid}
                                    className="flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-white"
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <img
                                        src={user.croppedAvatarUrl || defaultProjectPlaceHolder}
                                        alt={user.firstName}
                                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {user.firstName} {user.lastName}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {isOwner ? 'Owner' : 'Viewer'}
                                        </p>
                                      </div>
                                    </div>
                                    {manageMembersGroup?.isOwner && !isOwner && (
                                      <button
                                        onClick={() => handleRemoveMember(user.uuid)}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full flex-shrink-0 ml-2"
                                        title="Remove access"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Footer - Active Status and Archive */}
                      {manageMembersGroup.isOwner && (
                        <div className="border-t border-gray-200 bg-gray-50 p-4 flex-shrink-0 space-y-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full flex-shrink-0">
                              <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span className="font-medium text-green-700">Active</span>
                            <span className="text-xs">Viewers will receive email and in-app notifications.</span>
                          </div>

                          <button
                            onClick={() => {
                              if (manageMembersGroup) {
                                handleArchive(manageMembersGroup.id);
                              }
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                            Archive Collection
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop View (768px and above) - Two Column Layout - UNCHANGED */}
            <div
              className="hidden md:flex bg-white rounded-xl shadow-xl w-full max-w-4xl mx-auto overflow-hidden flex-col"
              style={{ maxHeight: '85vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Desktop Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {manageMembersGroup.isOwner ? 'Manage Members for' : 'View Members of'} {manageMembersGroup.title} collection
                  </h2>
                </div>
                <button
                  onClick={() => setManageMembersGroup(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Desktop Two Column Layout */}
              <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(85vh - 5rem)' }}>
                {/* Left Column - Add Members (only if owner) */}
                {manageMembersGroup?.isOwner && (
                  <div className="w-1/2 flex flex-col border-r border-gray-200 bg-white overflow-hidden">
                    {/* Header - Fixed */}
                    <div className="p-6 border-b border-gray-200 flex-shrink-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        Add Members
                      </h3>
                    </div>

                    {/* Search - Fixed */}
                    <div className="px-6 pt-4 pb-2 flex-shrink-0">
                      <input
                        type="text"
                        placeholder="Search members..."
                        value={searchMember}
                        onChange={(e) => setSearchMember(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    {/* Available Members List - Scrollable */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                      <div className="space-y-2">
                        {availableUsersForManage
                          .filter((user) =>
                            `${user.firstName} ${user.lastName}`
                              .toLowerCase()
                              .includes(searchMember.toLowerCase())
                          )
                          .map((user) => {
                            const isSelected = selectedMembers.includes(user.uuid);

                            return (
                              <div
                                key={user.uuid}
                                onClick={() => toggleMemberSelection(user.uuid)}
                                className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${isSelected
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'border-gray-200 hover:bg-gray-50'
                                  }`}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <img
                                    src={user.croppedAvatarUrl}
                                    onError={(e) => {
                                      e.currentTarget.src = defaultProjectPlaceHolder;
                                    }}
                                    alt={user.firstName}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {user.firstName} {user.lastName}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                    }`}>
                                    {isSelected && (
                                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        {availableUsersForManage.filter((user) =>
                          `${user.firstName} ${user.lastName}`
                            .toLowerCase()
                            .includes(searchMember.toLowerCase())
                        ).length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                              <p className="text-sm">No available members found</p>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Add Button - Fixed at Bottom */}
                    {selectedMembers.length > 0 && (
                      <div className="p-6 border-t border-gray-200 bg-white flex-shrink-0">
                        <button
                          onClick={handleAddMembers}
                          className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add {selectedMembers.length} User{selectedMembers.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Right Column - Current Members */}
                <div className={`flex flex-col bg-white overflow-hidden ${!manageMembersGroup?.isOwner ? 'w-full' : 'w-1/2'}`}>
                  {/* Header - Fixed */}
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Current Members
                      <span className="ml-2 text-base font-normal text-gray-500">
                        ({currentMembers.length})
                      </span>
                    </h3>
                  </div>

                  {/* Current Members List - Scrollable */}
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    {currentMembers.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="text-sm">No members in this collection yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {currentMembers
                          .filter((user) =>
                            `${user.firstName} ${user.lastName}`
                              .toLowerCase()
                              .includes(searchMember.toLowerCase())
                          )
                          .map((user) => {
                            const isOwner = user.uuid === currentUser.uuid;

                            return (
                              <div
                                key={user.uuid}
                                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <img
                                    src={user.croppedAvatarUrl}
                                    onError={(e) => {
                                      e.currentTarget.src = defaultProjectPlaceHolder;
                                    }}
                                    alt={user.firstName}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {user.firstName} {user.lastName}
                                    </p>
                                    {isOwner && (
                                      <p className="text-xs text-gray-500">Owner</p>
                                    )}
                                  </div>
                                </div>
                                {manageMembersGroup?.isOwner && !isOwner && (
                                  <button
                                    onClick={() => {
                                      if (!isArchived()) {
                                        handleRemoveMember(user.uuid);
                                      }
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0 ml-2"
                                    title="Remove member"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  {/* Desktop Footer - Fixed */}
                  {manageMembersGroup.shared && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4 flex items-center justify-between flex-shrink-0">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full">
                          <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="font-medium text-green-700">Active</span>
                        <span className="ml-2">Viewers will receive email and in-app notifications.</span>
                      </div>
                      {manageMembersGroup.isOwner && (
                        <button
                          onClick={() => {
                            if (!isArchived()) {
                              handleArchive(manageMembersGroup.id)
                            }
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                          Archive Collection
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}